import * as Dialog from '@radix-ui/react-dialog'
import { CollectionItemDetail } from '../../data/mockData'
import { useEffect, useCallback } from 'react'

interface ItemModalProps {
  item: CollectionItemDetail | null
  isOpen: boolean
  onClose: () => void
  allItems?: CollectionItemDetail[]
  onItemSelect?: (item: CollectionItemDetail) => void
}

export default function ItemModal({ item, isOpen, onClose, allItems = [], onItemSelect }: ItemModalProps) { 
  if (!item) return null
  
  // Helper function to determine item type based on URL or name
  const getItemType = (item: CollectionItemDetail): string => {
    const name = item.name.toLowerCase()
    const url = item.url?.toLowerCase() || ''
    
    if (name.includes('design') || name.includes('wireframe') || name.includes('mood board')) return 'Design'
    if (name.includes('research') || name.includes('ux')) return 'Research'
    if (name.includes('photo') || name.includes('image')) return 'Image'
    if (name.includes('spec') || name.includes('requirement') || name.includes('doc')) return 'Document'
    if (name.includes('meeting') || name.includes('notes')) return 'Notes'
    if (name.includes('budget') || name.includes('proposal')) return 'Finance'
    if (name.includes('sketch') || name.includes('drawing')) return 'Sketch'
    if (name.includes('plan') || name.includes('floor')) return 'Plan'
    if (url.includes('figma') || url.includes('design')) return 'Design'
    if (url.includes('docs') || url.includes('notion')) return 'Document'
    if (url.includes('spotify') || url.includes('podcast')) return 'Audio'
    
    return 'Item'
  }
  
  // Get current item index and navigation state
  const currentIndex = allItems.findIndex(i => i.id === item.id)
  const isFirstItem = currentIndex === 0
  const isLastItem = currentIndex === allItems.length - 1
  
  // Debug: Let's add some logging to see what's happening
  console.log('Debug - currentIndex:', currentIndex, 'allItems.length:', allItems.length, 'isLastItem:', isLastItem)
  
  // Handle sidebar navigation and item selection
  const handleNavigateItem = useCallback((direction: 'up' | 'down') => {
    console.log('handleNavigateItem called with direction:', direction)
    if (!onItemSelect || allItems.length === 0) return
    
    // Get fresh current index
    const freshCurrentIndex = allItems.findIndex(i => i.id === item.id)
    console.log('Fresh currentIndex:', freshCurrentIndex, 'allItems.length:', allItems.length)
    if (freshCurrentIndex === -1) return
    
    let nextIndex
    if (direction === 'up' && freshCurrentIndex > 0) {
      nextIndex = freshCurrentIndex - 1
      console.log('Moving up to index:', nextIndex)
    } else if (direction === 'down' && freshCurrentIndex < allItems.length - 1) {
      nextIndex = freshCurrentIndex + 1
      console.log('Moving down to index:', nextIndex)
    } else {
      console.log('At boundary - not navigating. Direction:', direction, 'freshCurrentIndex:', freshCurrentIndex)
      return // Don't navigate if at boundary
    }
    
    const nextItem = allItems[nextIndex]
    onItemSelect(nextItem)
    
    // Scroll the selected item into view
    setTimeout(() => {
      const sidebar = document.querySelector('.sidebar-scroll-container')
      const itemButton = document.querySelector(`[data-item-id="${nextItem.id}"]`)
      if (sidebar && itemButton) {
        const sidebarRect = sidebar.getBoundingClientRect()
        const itemRect = itemButton.getBoundingClientRect()
        
        // Check if item is outside visible area
        if (itemRect.top < sidebarRect.top || itemRect.bottom > sidebarRect.bottom) {
          itemButton.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
      }
    }, 50)
  }, [onItemSelect, allItems, item.id])

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle arrow keys when modal is open
      if (!isOpen || allItems.length === 0) return
      
      // Prevent default behavior for arrow keys
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault()
        
        if (event.key === 'ArrowUp') {
          handleNavigateItem('up')
        } else if (event.key === 'ArrowDown') {
          handleNavigateItem('down')
        }
      }
    }

    // Add event listener when modal is open
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
    }

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, handleNavigateItem, allItems.length])

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 z-[1000]" />
        <Dialog.Content className="fixed top-12 left-0 right-0 bottom-0 rounded-t-2xl overflow-hidden flex flex-col z-[1000] focus:outline-none md:top-36 md:left-6 md:right-6 md:rounded-t-2xl md:rounded-b-none md:shadow-2xl md:w-auto md:max-w-none md:h-auto md:max-h-none md:flex-row lg:top-24 lg:left-8 lg:right-8 lg:bottom-0 lg:rounded-b-none xl:left-1/2 xl:transform xl:-translate-x-1/2 xl:w-[1200px] xl:bottom-0 xl:rounded-b-none" style={{backgroundColor: '#FDFDFD'}}>
          
          {/* Close Button - positioned outside content on xl screens */}
          <Dialog.Close asChild>
            <button className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white shadow-lg sm:flex items-center justify-center hover:bg-gray-50 transition-colors xl:block hidden z-10">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </Dialog.Close>
          
          {/* Side Menu - Hidden on mobile, wider on XL */}
          {allItems.length > 0 && (
            <div className="hidden md:flex md:flex-col md:w-40 xl:w-80 md:p-4 md:pt-8 xl:p-6 xl:pt-8">
              <div className="bg-white rounded-xl overflow-hidden h-full flex flex-col">
                <div className="p-3 xl:p-4 flex items-center justify-between">
                  <h3 className="text-sm xl:text-base font-semibold text-gray-600 tracking-wide">More from this collection</h3>
                  <div className="hidden xl:flex gap-1">
                    <button
                      onClick={() => handleNavigateItem('up')}
                      disabled={isFirstItem}
                      className={`w-6 h-6 rounded-full transition-colors flex items-center justify-center ${
                        isFirstItem 
                          ? 'bg-gray-50 text-gray-300 cursor-not-allowed' 
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                      }`}
                      title={isFirstItem ? 'No previous item' : 'Previous item'}
                    >
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 7.5L6 4.5L3 7.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                    <button
                      onClick={() => handleNavigateItem('down')}
                      disabled={isLastItem}
                      className={`w-6 h-6 rounded-full transition-colors flex items-center justify-center ${
                        isLastItem 
                          ? 'bg-gray-50 text-gray-300 cursor-not-allowed' 
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                      }`}
                      title={isLastItem ? 'No next item' : 'Next item'}
                    >
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-2 xl:p-3 sidebar-scroll-container">
                {allItems.map((collectionItem) => (
                  <button
                    key={collectionItem.id}
                    data-item-id={collectionItem.id}
                    onClick={() => onItemSelect?.(collectionItem)}
                    className={`w-full p-3 xl:p-4 flex items-center xl:items-start gap-3 xl:gap-4 hover:bg-gray-50 transition-colors rounded-lg mb-2 xl:mb-3 text-left ${
                      collectionItem.id === item.id ? 'bg-gray-50 border border-gray-200 shadow-sm' : ''
                    }`}
                  >
                    {/* Image */}
                    <div className="w-12 h-12 xl:w-16 xl:h-16 rounded-lg overflow-hidden flex-shrink-0">
                      <img 
                        src={collectionItem.imagePath} 
                        alt={collectionItem.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    
                    {/* Details - only show on XL screens */}
                    <div className="hidden xl:flex xl:flex-col xl:flex-1 xl:min-w-0">
                      <h4 className="text-sm font-medium text-gray-900 truncate mb-1">
                        {collectionItem.name}
                      </h4>
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-md self-start">
                        {getItemType(collectionItem)}
                      </span>
                    </div>
                  </button>
                ))}
                </div>
              </div>
            </div>
          )}

          {/* Main Content */}
          <div className="flex-1 flex flex-col overflow-hidden md:p-4 xl:flex-row xl:gap-8 xl:p-8">
            
            {/* Mobile: Full width image */}
            <div className="w-full aspect-video bg-gray-100 flex items-center justify-center overflow-hidden md:hidden">
              <img src={item.imagePath} alt={item.name} className="w-full h-full object-cover" />
            </div>

            {/* Tablet (md-lg): Split layout with title/tags on left, image on right */}
            <div className="hidden md:flex md:gap-6 md:py-8 lg:py-8 xl:hidden">
              <div className="md:w-1/2 md:flex md:flex-col">
                <Dialog.Title className="text-lg md:text-xl font-semibold text-gray-900 leading-tight mb-6">
                  {item.name}
                </Dialog.Title>
                <div className="flex gap-2 mb-5">
                  <span className="bg-gray-100 text-gray-600 px-3 py-1.5 rounded-2xl text-sm font-medium">PRD</span>
                  <span className="bg-gray-100 text-gray-600 px-3 py-1.5 rounded-2xl text-sm font-medium">Summary page</span>
                </div>
              </div>
              <div className="md:w-1/2 md:max-w-md bg-gray-100 flex items-center justify-center overflow-hidden rounded-lg" style={{aspectRatio: '16/9'}}>
                <img src={item.imagePath} alt={item.name} className="w-full h-full object-cover" />
              </div>
            </div>

            {/* Left Column: Textual Content (XL screens) */}
            <div className="xl:w-2/3 xl:flex xl:flex-col xl:overflow-y-auto">
              <div className="p-4 md:p-0 md:px-6 lg:px-8 xl:p-0 flex-1 overflow-y-auto">
                {/* Mobile & XL: Title and tags */}
                <div className="md:hidden xl:block">
                  <Dialog.Title className="text-lg md:text-xl xl:text-2xl font-semibold text-gray-900 leading-tight mb-6">
                    {item.name}
                  </Dialog.Title>
                  <div className="flex gap-2 mb-6 xl:mb-12">
                    <span className="bg-gray-100 text-gray-600 px-3 py-1.5 rounded-2xl text-sm font-medium">PRD</span>
                    <span className="bg-gray-100 text-gray-600 px-3 py-1.5 rounded-2xl text-sm font-medium">Summary page</span>
                  </div>
                </div>

                <Dialog.Description className="text-base leading-relaxed text-gray-700 mb-6 xl:text-lg xl:mb-8">
                  {item.description}
                </Dialog.Description>

                {item.url && (
                  <div className="mb-8">
                    <a 
                      href={item.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-blue-600 font-medium text-base hover:opacity-80 transition-opacity xl:text-lg"
                    >
                      Visit Link
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 8.66667V12.6667C12 13.0203 11.8595 13.3594 11.6095 13.6095C11.3594 13.8595 11.0203 14 10.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V5.33333C2 4.97971 2.14048 4.64057 2.39052 4.39052C2.64057 4.14048 2.97971 4 3.33333 4H7.33333M10 2H14M14 2V6M14 2L6.66667 9.33333" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </a>
                  </div>
                )}

                <div className="mb-6 xl:mb-8">
                  <h3 className="text-sm font-semibold text-gray-600 tracking-wide mb-3 xl:text-base xl:mb-4">KEY TAKEAWAYS</h3>
                  <p className="text-sm leading-relaxed text-gray-700 m-0 xl:text-base xl:leading-relaxed">
                    As humans, we have an underlying "blueprint" for how we perceive and process the world around us, 
                    and the study of psychology helps us decipher this blueprint. Designers can use this knowledge to build 
                    more intuitive, human-centered products and experiences. Instead of forcing users to adapt to the 
                    design of a product or experience, we can use some key principles from psychology as a guide for 
                    designing in a way that is adapted to people.
                  </p>
                </div>
              </div>
            </div>

            {/* Right Column: Image and Date (XL screens) */}
            <div className="hidden xl:flex xl:w-1/3 xl:flex-col xl:gap-6">
              <div className="bg-gray-100 flex items-center justify-center overflow-hidden rounded-xl flex-1 max-h-96">
                <img src={item.imagePath} alt={item.name} className="w-full h-full object-cover" />
              </div>
              
              <div className="text-gray-500 text-sm xl:text-base">
                <span>Saved on July 12</span>
                <br />
                <span>via WhatsApp</span>
                <br />
                <span>Forwarded by You</span>
              </div>
            </div>

            {/* Date for smaller screens */}
            <div className="xl:hidden p-4 md:p-0 md:px-6 lg:px-8">
              <div className="text-gray-500 text-sm">
                <span>Added {item.dateCreated}</span>
              </div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}